// Measure It generator. Every drawn object has a true size; the ruler, scale and jug are
// drawn honestly to that size so measuring (not guessing) gives the answer.

import { int, pick, shuffle, type Rng } from '@kit/rng';

export type MeasureKind = 'length' | 'compare' | 'weight' | 'capacity' | 'convert' | 'scale' | 'rate';
export type ObjKind = 'pencil' | 'crayon' | 'spoon' | 'book' | 'shoe' | 'carrot' | 'ribbon' | 'stick' | 'apple' | 'cat' | 'dog' | 'melon' | 'rock' | 'backpack' | 'cup' | 'bottle' | 'bucket' | 'jug' | 'road' | 'river';

export interface MeasureProblem {
  kind: MeasureKind;
  object: ObjKind;
  objectName: string;
  /** true measurement in `unit` (the thing she must read off the tool) */
  value: number;
  unit: string;
  /** what she must enter or pick; equals value except for convert/scale/rate */
  answer: number;
  answerUnit: string;
  question: string;
  /** for the estimate step and, at low rungs, the answer step */
  choices: number[];
  useNumpad: boolean;
  /** tool scale max (ruler length, dial max, jug max) in `unit` */
  toolMax: number;
  /** tick spacing on the tool in `unit` */
  tick: number;
  hint: string;
  /** compare kind: second object */
  second?: { object: ObjKind; objectName: string; value: number };
  /** compare kind: the right word */
  compareAnswer?: string;
  compareChoices?: string[];
  /** convert/scale/rate: factor explanation */
  factorText?: string;
}

const SMALL: { k: ObjKind; name: string; lo: number; hi: number }[] = [
  { k: 'pencil', name: 'pencil', lo: 12, hi: 19 }, { k: 'crayon', name: 'crayon', lo: 6, hi: 10 }, { k: 'spoon', name: 'spoon', lo: 14, hi: 20 },
  { k: 'book', name: 'book', lo: 15, hi: 24 }, { k: 'shoe', name: 'shoe', lo: 16, hi: 25 }, { k: 'carrot', name: 'carrot', lo: 9, hi: 18 },
  { k: 'ribbon', name: 'ribbon', lo: 5, hi: 24 }, { k: 'stick', name: 'stick', lo: 8, hi: 25 },
];
const HEAVY_G: { k: ObjKind; name: string; lo: number; hi: number }[] = [
  { k: 'apple', name: 'apple', lo: 100, hi: 250 }, { k: 'book', name: 'book', lo: 300, hi: 900 }, { k: 'shoe', name: 'shoe', lo: 200, hi: 600 }, { k: 'rock', name: 'rock', lo: 150, hi: 950 }, { k: 'cup', name: 'cup', lo: 150, hi: 400 },
];
const HEAVY_KG: { k: ObjKind; name: string; lo: number; hi: number }[] = [
  { k: 'cat', name: 'cat', lo: 3, hi: 6 }, { k: 'dog', name: 'dog', lo: 5, hi: 9 }, { k: 'melon', name: 'melon', lo: 2, hi: 5 }, { k: 'backpack', name: 'backpack', lo: 2, hi: 7 },
];
const CAP_ML: { k: ObjKind; name: string; lo: number; hi: number }[] = [
  { k: 'cup', name: 'cup', lo: 100, hi: 400 }, { k: 'bottle', name: 'bottle', lo: 300, hi: 900 },
];
const CAP_L: { k: ObjKind; name: string; lo: number; hi: number }[] = [
  { k: 'bucket', name: 'bucket', lo: 2, hi: 9 }, { k: 'jug', name: 'jug', lo: 1, hi: 4 },
];

function spreadChoices(answer: number, step: number, rng: Rng, count = 4): number[] {
  const set = new Set<number>([answer]);
  let guard = 0;
  while (set.size < count && guard++ < 60) {
    const d = (int(rng, 1, 3)) * step * (rng() < 0.5 ? -1 : 1);
    const v = round2(answer + d);
    if (v > 0) set.add(v);
  }
  return shuffle(rng, [...set]);
}

export function round2(n: number): number { return Math.round(n * 100) / 100; }

export function fmtNum(n: number): string { return Number.isInteger(n) ? String(n) : String(round2(n)); }

export function makeMeasure(level: number, rng: Rng, index: number): MeasureProblem {
  const L = Math.max(1, Math.min(10, level));
  if (L <= 2) return index % 3 === 2 ? compare(rng) : lengthCm(rng, false);
  if (L <= 4) return index % 2 === 0 ? weight(rng) : capacity(rng);
  if (L <= 6) return index % 3 === 0 ? lengthIn(rng) : index % 3 === 1 ? lengthCm(rng, true) : (rng() < 0.5 ? weight(rng) : capacity(rng));
  if (L <= 8) return index % 3 === 2 ? lengthIn(rng) : convert(rng);
  return index % 2 === 0 ? scaleMap(rng) : rate(rng);
}

function lengthCm(rng: Rng, halves: boolean): MeasureProblem {
  const o = pick(rng, SMALL);
  let value = int(rng, o.lo, o.hi);
  if (halves && rng() < 0.6) value = value - 0.5;
  const toolMax = 25;
  return {
    kind: 'length', object: o.k, objectName: o.name, value, unit: 'cm', answer: value, answerUnit: 'cm',
    question: `How long is the ${o.name}, in centimeters?`,
    choices: spreadChoices(value, halves ? 0.5 : 1, rng), useNumpad: false, toolMax, tick: halves ? 0.5 : 1,
    hint: 'Line up the 0 on the ruler with the left end, then read the number at the right end.',
  };
}

function lengthIn(rng: Rng): MeasureProblem {
  const o = pick(rng, SMALL);
  const value = int(rng, 4, 20) / 2; // 2 .. 10 inches in halves
  return {
    kind: 'length', object: o.k, objectName: o.name, value, unit: 'in', answer: value, answerUnit: 'in',
    question: `How long is the ${o.name}, in inches? (The small marks are halves.)`,
    choices: spreadChoices(value, 0.5, rng), useNumpad: false, toolMax: 12, tick: 0.5,
    hint: 'Each inch is split by a short mark at the half. Count whole inches, then add a half if needed.',
  };
}

function compare(rng: Rng): MeasureProblem {
  const [a, b] = shuffle(rng, SMALL).slice(0, 2);
  let va = int(rng, a.lo, a.hi), vb = int(rng, b.lo, b.hi);
  if (va === vb) vb = Math.min(25, vb + 2);
  const longer = va > vb ? a : b;
  return {
    kind: 'compare', object: a.k, objectName: a.name, value: va, unit: 'cm', answer: longer === a ? 0 : 1, answerUnit: '',
    question: `Which is longer, the ${a.name} or the ${b.name}?`,
    choices: [], useNumpad: false, toolMax: 25, tick: 1,
    hint: 'Line both up against the ruler. The one that reaches a bigger number is longer.',
    second: { object: b.k, objectName: b.name, value: vb },
    compareAnswer: longer.name, compareChoices: [a.name, b.name],
  };
}

function weight(rng: Rng): MeasureProblem {
  if (rng() < 0.5) {
    const o = pick(rng, HEAVY_G);
    const value = Math.round(int(rng, o.lo, o.hi) / 50) * 50;
    return { kind: 'weight', object: o.k, objectName: o.name, value, unit: 'g', answer: value, answerUnit: 'g', question: `How heavy is the ${o.name}, in grams?`, choices: spreadChoices(value, 50, rng), useNumpad: false, toolMax: 1000, tick: 50, hint: 'Put it on the scale and read where the needle stops. Each small mark is 50 g.' };
  }
  const o = pick(rng, HEAVY_KG);
  const value = int(rng, o.lo, o.hi);
  return { kind: 'weight', object: o.k, objectName: o.name, value, unit: 'kg', answer: value, answerUnit: 'kg', question: `How heavy is the ${o.name}, in kilograms?`, choices: spreadChoices(value, 1, rng), useNumpad: false, toolMax: 10, tick: 1, hint: 'Put it on the scale and read where the needle stops. Each mark is 1 kg.' };
}

function capacity(rng: Rng): MeasureProblem {
  if (rng() < 0.5) {
    const o = pick(rng, CAP_ML);
    const value = Math.round(int(rng, o.lo, o.hi) / 100) * 100;
    return { kind: 'capacity', object: o.k, objectName: o.name, value, unit: 'ml', answer: value, answerUnit: 'ml', question: `How much does the ${o.name} hold, in milliliters?`, choices: spreadChoices(value, 100, rng), useNumpad: false, toolMax: 1000, tick: 100, hint: 'Pour it into the measuring jug and read the line the water reaches. Each line is 100 ml.' };
  }
  const o = pick(rng, CAP_L);
  const value = int(rng, o.lo, o.hi);
  return { kind: 'capacity', object: o.k, objectName: o.name, value, unit: 'L', answer: value, answerUnit: 'L', question: `How much does the ${o.name} hold, in liters?`, choices: spreadChoices(value, 1, rng), useNumpad: false, toolMax: 10, tick: 1, hint: 'Pour it into the big jug and read the line. Each line is 1 liter.' };
}

function convert(rng: Rng): MeasureProblem {
  const kind = int(rng, 0, 4);
  let value: number, unit: string, answer: number, answerUnit: string, factorText: string, toolMax: number, tick: number;
  switch (kind) {
    case 0: value = int(rng, 1, 9); unit = 'm'; answer = value * 100; answerUnit = 'cm'; factorText = '1 m = 100 cm'; toolMax = 10; tick = 1; break;
    case 1: value = int(rng, 2, 9) * 100; unit = 'cm'; answer = value / 100; answerUnit = 'm'; factorText = '100 cm = 1 m'; toolMax = 1000; tick = 100; break;
    case 2: value = int(rng, 1, 6) * 12; unit = 'in'; answer = value / 12; answerUnit = 'ft'; factorText = '12 in = 1 ft'; toolMax = 72; tick = 12; break;
    case 3: value = int(rng, 1, 6); unit = 'ft'; answer = value * 12; answerUnit = 'in'; factorText = '1 ft = 12 in'; toolMax = 6; tick = 1; break;
    default: value = int(rng, 1, 5); unit = 'kg'; answer = value * 1000; answerUnit = 'g'; factorText = '1 kg = 1000 g'; toolMax = 5; tick = 1; break;
  }
  return {
    kind: 'convert', object: 'ribbon', objectName: 'ribbon', value, unit, answer, answerUnit,
    question: `${fmtNum(value)} ${unit} is how many ${answerUnit}?`,
    choices: [], useNumpad: true, toolMax, tick, factorText,
    hint: `${factorText}. Count along the double ruler: every ${unit} line has a ${answerUnit} number under it.`,
  };
}

function scaleMap(rng: Rng): MeasureProblem {
  const per = pick(rng, [2, 5, 10, 25, 50]);
  const cm = int(rng, 2, 8);
  const answer = cm * per;
  return {
    kind: 'scale', object: rng() < 0.5 ? 'road' : 'river', objectName: rng() < 0.5 ? 'road' : 'river', value: cm, unit: 'cm', answer, answerUnit: 'km',
    question: `On this map, 1 cm stands for ${per} km. How many km long is it really?`,
    choices: [], useNumpad: true, toolMax: 10, tick: 1, factorText: `1 cm = ${per} km`,
    hint: `Measure it in cm on the map, then multiply by ${per}.`,
  };
}

function rate(rng: Rng): MeasureProblem {
  const perMin = int(rng, 2, 9);
  const mins = int(rng, 3, 9);
  const answer = perMin * mins;
  const who = pick(rng, ['A snail', 'A toy car', 'Your pet', 'A beetle']);
  return {
    kind: 'rate', object: 'stick', objectName: 'track', value: perMin, unit: 'cm', answer, answerUnit: 'cm',
    question: `${who} moves ${perMin} cm every minute. How far does it go in ${mins} minutes?`,
    choices: [], useNumpad: true, toolMax: Math.max(10, Math.ceil(answer / 10) * 10), tick: perMin, factorText: `${perMin} cm each minute, ${mins} minutes`,
    hint: `Each minute adds ${perMin} cm. ${mins} minutes means ${perMin} added ${mins} times.`,
  };
}

/** estimate within 25% counts as a good eye */
export function goodEye(estimate: number, answer: number): boolean {
  return Math.abs(estimate - answer) <= Math.max(0.5, answer * 0.25);
}
